import { useQuery } from '@tanstack/react-query';
import { StorageKeys, useStorageKeys } from '../../storage';
import { SortDirectionEnum } from '../../graphql';
import { useGrippContext } from '../../grippContext';
import { useCurrentWorkspace } from '../../auth';

export type UseListOrderResult = {
  data: any;
  isLoading: boolean;
};

export const useListOrder = (
  queryKey: string,
  storageKey: string,
  isMultiWorkspace?: boolean
): UseListOrderResult => {
  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const currentWorkspace = useCurrentWorkspace();
  const workspace = isMultiWorkspace
    ? currentWorkspace === null
      ? 'ME'
      : currentWorkspace?.id
    : undefined;

  const { data, isLoading } = useQuery<any>({
    queryKey: [queryKey, workspace],
    enabled: !isMultiWorkspace || currentWorkspace !== undefined,
    queryFn: async () => {
      const defaultOrder = {
        field: 'name',
        order: SortDirectionEnum.Asc,
      };
      const parsed = JSON.parse(
        (await storageHandler.getItem(
          storageKeys[storageKey as keyof StorageKeys]
        )) as string
      );

      let filter = parsed;
      if (isMultiWorkspace && workspace) {
        if (
          Array.isArray(parsed) &&
          parsed.every(
            (item) =>
              item &&
              typeof item === 'object' &&
              'workspace' in item &&
              'filter' in item
          )
        ) {
          // Valid new format
          const match = parsed.find((item) => item.workspace === workspace);
          if (match) {
            filter = match.filter;
          } else {
            filter = undefined;
          }
        } else {
          // Old format detected, discard
          filter = undefined;
        }
      }
      return filter && isValidOrder(filter) ? filter : defaultOrder;
    },
  });

  return { data, isLoading };
};

const isValidOrder = (obj: any): obj is { field: any; order: any } => {
  return (
    typeof obj === 'object' && obj !== null && 'field' in obj && 'order' in obj
  );
};
